import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const AdminContracts = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  
  // Bulk actions
  const [selectedContracts, setSelectedContracts] = useState(new Set());
  
  // Form data
  const [formData, setFormData] = useState({
    contract_name: '',
    company_id: '',
    product_id: '',
    category_id: '',
    contract_type: 'percentage_discount',
    base_price: '',
    discount_percentage: '',
    volume_tiers: [],
    payment_terms: 30,
    credit_limit: '',
    minimum_order_value: '',
    free_shipping_threshold: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: '',
    terms_and_conditions: '',
    internal_notes: ''
  });

  // Volume tier form
  const [newTier, setNewTier] = useState({
    min_qty: '',
    max_qty: '',
    discount: ''
  });

  useEffect(() => {
    fetchContracts();
    fetchCompanies();
    fetchProducts();
    fetchCategories();
  }, [currentPage, searchTerm, selectedCompany, selectedStatus, selectedType]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      
      if (selectedCompany) params.append('company_id', selectedCompany);
      if (selectedStatus) params.append('status', selectedStatus);
      
      const response = await axios.get(`/api/contract-pricing/contracts?${params}`);
      setContracts(response.data.contracts || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      setError('שגיאה בטעינת החוזים');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data.companies || response.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setCompanies([]);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      // Fetch all products by making multiple requests if needed
      let allProducts = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(`/api/products?limit=100&page=${page}`);
        const products = response.data.products || [];
        allProducts = [...allProducts, ...products];
        
        // Check if there are more pages
        const pagination = response.data.pagination;
        hasMore = pagination && page < pagination.totalPages;
        page++;
      }
      
      setProducts(allProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  const prepareContractData = (data) => {
    const contractData = { ...data };
    
    // Convert and validate numeric fields
    if (contractData.base_price) {
      contractData.base_price = parseFloat(contractData.base_price);
    } else {
      delete contractData.base_price;
    }
    
    if (contractData.discount_percentage) {
      contractData.discount_percentage = parseFloat(contractData.discount_percentage) / 100;
    } else {
      delete contractData.discount_percentage;
    }
    
    if (contractData.credit_limit) {
      contractData.credit_limit = parseFloat(contractData.credit_limit);
    } else {
      delete contractData.credit_limit;
    }
    
    if (contractData.minimum_order_value) {
      contractData.minimum_order_value = parseFloat(contractData.minimum_order_value);
    } else {
      delete contractData.minimum_order_value;
    }
    
    if (contractData.free_shipping_threshold) {
      contractData.free_shipping_threshold = parseFloat(contractData.free_shipping_threshold);
    } else {
      delete contractData.free_shipping_threshold;
    }
    
    // Handle optional string fields
    if (!contractData.product_id) {
      delete contractData.product_id;
    }
    
    if (!contractData.category_id) {
      delete contractData.category_id;
    }
    
    if (!contractData.valid_to) {
      delete contractData.valid_to;
    }
    
    return contractData;
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    try {
      const contractData = prepareContractData(formData);

      await axios.post('/api/contract-pricing/contracts', contractData);
      setShowCreateModal(false);
      resetForm();
      fetchContracts();
      window.alert('החוזה נוצר בהצלחה');
    } catch (err) {
      console.error('Error creating contract:', err);
      window.alert('שגיאה ביצירת החוזה');
    }
  };

  const handleUpdateContract = async (e) => {
    e.preventDefault();
    try {
      const contractData = prepareContractData(formData);

      await axios.put(`/api/contract-pricing/contracts/${selectedContract.id}`, contractData);
      setShowEditModal(false);
      setSelectedContract(null);
      resetForm();
      fetchContracts();
      window.alert('החוזה עודכן בהצלחה');
    } catch (err) {
      console.error('Error updating contract:', err);
      window.alert('שגיאה בעדכון החוזה');
    }
  };

  const handleApproveContract = async (contractId) => {
    try {
      await axios.patch(`/api/contract-pricing/contracts/${contractId}/status`, {
        status: 'active'
      });
      fetchContracts();
      window.alert('החוזה אושר והופעל בהצלחה');
    } catch (err) {
      console.error('Error approving contract:', err);
      window.alert('שגיאה באישור החוזה');
    }
  };

  const handleRejectContract = async (contractId) => {
    const reason = window.prompt('סיבת דחיית החוזה (אופציונלי):');
    if (reason === null) return; // User cancelled
    
    try {
      await axios.patch(`/api/contract-pricing/contracts/${contractId}/status`, {
        status: 'cancelled'
      });
      fetchContracts();
      window.alert('החוזה נדחה');
    } catch (err) {
      console.error('Error rejecting contract:', err);
      window.alert('שגיאה בדחיית החוזה');
    }
  };

  const handleChangeContractStatus = async (contractId, newStatus) => {
    try {
      await axios.patch(`/api/contract-pricing/contracts/${contractId}/status`, {
        status: newStatus
      });
      fetchContracts();
      window.alert(`סטטוס החוזה עודכן ל-${getStatusText(newStatus)}`);
    } catch (err) {
      console.error('Error changing contract status:', err);
      window.alert('שגיאה בעדכון סטטוס החוזה');
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      draft: 'טיוטה',
      pending_approval: 'ממתין לאישור',
      active: 'פעיל',
      expired: 'פג תוקף',
      cancelled: 'מבוטל'
    };
    return statusTexts[status] || status;
  };

  // Bulk actions
  const toggleContractSelection = (contractId) => {
    const newSelected = new Set(selectedContracts);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContracts(newSelected);
  };

  const toggleAllContracts = () => {
    if (selectedContracts.size === contracts.length) {
      setSelectedContracts(new Set());
    } else {
      setSelectedContracts(new Set(contracts.map(c => c.id)));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedContracts.size === 0) return;
    
    if (!window.confirm(`האם אתה בטוח שברצונך לשנות את סטטוס ${selectedContracts.size} חוזים ל-${getStatusText(newStatus)}?`)) return;
    
    try {
      const promises = Array.from(selectedContracts).map(contractId =>
        axios.patch(`/api/contract-pricing/contracts/${contractId}/status`, { status: newStatus })
      );
      
      await Promise.all(promises);
      setSelectedContracts(new Set());
      fetchContracts();
      window.alert(`${selectedContracts.size} חוזים עודכנו בהצלחה`);
    } catch (err) {
      console.error('Error updating contracts:', err);
      window.alert('שגיאה בעדכון החוזים');
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את החוזה?')) return;
    
    try {
      await axios.delete(`/api/contract-pricing/contracts/${contractId}`);
      fetchContracts();
      window.alert('החוזה נמחק בהצלחה');
    } catch (err) {
      console.error('Error deleting contract:', err);
      window.alert('שגיאה במחיקת החוזה');
    }
  };

  const resetForm = () => {
    setFormData({
      contract_name: '',
      company_id: '',
      product_id: '',
      category_id: '',
      contract_type: 'percentage_discount',
      base_price: '',
      discount_percentage: '',
      volume_tiers: [],
      payment_terms: 30,
      credit_limit: '',
      minimum_order_value: '',
      free_shipping_threshold: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_to: '',
      terms_and_conditions: '',
      internal_notes: ''
    });
    setNewTier({ min_qty: '', max_qty: '', discount: '' });
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    setFormData({
      contract_name: contract.contract_name || '',
      company_id: contract.company_id || '',
      product_id: contract.product_id || '',
      category_id: contract.category_id || '',
      contract_type: contract.contract_type || 'percentage_discount',
      base_price: contract.base_price || '',
      discount_percentage: contract.discount_percentage ? (contract.discount_percentage * 100).toString() : '',
      volume_tiers: contract.volume_tiers || [],
      payment_terms: contract.payment_terms || 30,
      credit_limit: contract.credit_limit || '',
      minimum_order_value: contract.minimum_order_value || '',
      free_shipping_threshold: contract.free_shipping_threshold || '',
      valid_from: contract.valid_from ? contract.valid_from.split('T')[0] : '',
      valid_to: contract.valid_to ? contract.valid_to.split('T')[0] : '',
      terms_and_conditions: contract.terms_and_conditions || '',
      internal_notes: contract.internal_notes || ''
    });
    setShowEditModal(true);
  };

  const addVolumeTier = () => {
    if (!newTier.min_qty || !newTier.discount) return;
    
    const tier = {
      min_qty: parseInt(newTier.min_qty),
      max_qty: newTier.max_qty ? parseInt(newTier.max_qty) : null,
      discount: parseFloat(newTier.discount) / 100
    };
    
    setFormData(prev => ({
      ...prev,
      volume_tiers: [...prev.volume_tiers, tier]
    }));
    
    setNewTier({ min_qty: '', max_qty: '', discount: '' });
  };

  const removeVolumeTier = (index) => {
    setFormData(prev => ({
      ...prev,
      volume_tiers: prev.volume_tiers.filter((_, i) => i !== index)
    }));
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return `₪${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('he-IL');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', text: 'טיוטה' },
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', text: 'ממתין לאישור' },
      active: { color: 'bg-green-100 text-green-800', text: 'פעיל' },
      expired: { color: 'bg-red-100 text-red-800', text: 'פג תוקף' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'מבוטל' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      volume_discount: { color: 'bg-blue-100 text-blue-800', text: 'הנחת כמות' },
      fixed_price: { color: 'bg-purple-100 text-purple-800', text: 'מחיר קבוע' },
      percentage_discount: { color: 'bg-green-100 text-green-800', text: 'הנחה באחוזים' },
      tiered_pricing: { color: 'bg-orange-100 text-orange-800', text: 'תמחור מדורג' }
    };
    
    const config = typeConfig[type] || typeConfig.percentage_discount;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ניהול חוזים</h1>
          <p className="text-gray-600">ניהול חוזי תמחור ללקוחות עסקיים</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 ml-2" />
          חוזה חדש
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="חיפוש חוזים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">כל החברות</option>
            {(companies || []).map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
          
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">כל הסטטוסים</option>
            <option value="draft">טיוטה</option>
            <option value="pending_approval">ממתין לאישור</option>
            <option value="active">פעיל</option>
            <option value="expired">פג תוקף</option>
            <option value="cancelled">מבוטל</option>
          </select>
          
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">כל הסוגים</option>
            <option value="volume_discount">הנחת כמות</option>
            <option value="fixed_price">מחיר קבוע</option>
            <option value="percentage_discount">הנחה באחוזים</option>
            <option value="tiered_pricing">תמחור מדורג</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedContracts.size > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              נבחרו {selectedContracts.size} חוזים
            </span>
            <div className="flex items-center space-x-2 space-x-reverse">
              <button
                onClick={() => handleBulkStatusChange('active')}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
              >
                אשר הכל
              </button>
              <button
                onClick={() => handleBulkStatusChange('cancelled')}
                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
              >
                דחה הכל
              </button>
              <button
                onClick={() => handleBulkStatusChange('pending_approval')}
                className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
              >
                העבר לאישור
              </button>
              <button
                onClick={() => setSelectedContracts(new Set())}
                className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
              >
                בטל בחירה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contracts Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={contracts.length > 0 && selectedContracts.size === contracts.length}
                  onChange={toggleAllContracts}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                שם החוזה
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                חברה
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                סוג
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                סטטוס
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                תוקף
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                פעולות
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contracts.map((contract) => (
              <tr key={contract.id} className={`hover:bg-gray-50 ${selectedContracts.has(contract.id) ? 'bg-blue-50' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedContracts.has(contract.id)}
                    onChange={() => toggleContractSelection(contract.id)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 ml-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {contract.contract_name}
                      </div>
                      {contract.product && (
                        <div className="text-sm text-gray-500">
                          מוצר: {contract.product.name}
                        </div>
                      )}
                      {contract.category && (
                        <div className="text-sm text-gray-500">
                          קטגוריה: {contract.category.name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-4 w-4 text-gray-400 ml-1" />
                    <span className="text-sm text-gray-900">
                      {contract.company?.name}
                    </span>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {getTypeBadge(contract.contract_type)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(contract.status)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 text-gray-400 ml-1" />
                    <div>
                      <div>{formatDate(contract.valid_from)}</div>
                      {contract.valid_to && (
                        <div className="text-gray-500">
                          עד: {formatDate(contract.valid_to)}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    {/* Edit Button */}
                    <button
                      onClick={() => openEditModal(contract)}
                      className="text-blue-600 hover:text-blue-900"
                      title="ערוך חוזה"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Approval Actions for Pending Contracts */}
                    {contract.status === 'pending_approval' && (
                      <>
                        <button
                          onClick={() => handleApproveContract(contract.id)}
                          className="text-green-600 hover:text-green-900"
                          title="אשר חוזה"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRejectContract(contract.id)}
                          className="text-red-600 hover:text-red-900"
                          title="דחה חוזה"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    
                    {/* Status Change Dropdown for Active/Draft Contracts */}
                    {(contract.status === 'active' || contract.status === 'draft') && (
                      <select
                        value={contract.status}
                        onChange={(e) => handleChangeContractStatus(contract.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                        title="שנה סטטוס"
                      >
                        <option value="draft">טיוטה</option>
                        <option value="pending_approval">לאישור</option>
                        <option value="active">פעיל</option>
                        <option value="expired">פג תוקף</option>
                        <option value="cancelled">מבוטל</option>
                      </select>
                    )}
                    
                    {/* Reactivate Button for Expired/Cancelled Contracts */}
                    {(contract.status === 'expired' || contract.status === 'cancelled') && (
                      <button
                        onClick={() => handleChangeContractStatus(contract.id, 'active')}
                        className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded"
                        title="הפעל מחדש"
                      >
                        הפעל
                      </button>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteContract(contract.id)}
                      className="text-red-600 hover:text-red-900"
                      title="מחק חוזה"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {contracts.length === 0 && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">אין חוזים</h3>
            <p className="mt-1 text-sm text-gray-500">התחל על ידי יצירת חוזה חדש</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              הקודם
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              הבא
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                מציג עמוד {currentPage} מתוך {totalPages}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  הקודם
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  הבא
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Contract Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {showCreateModal ? 'חוזה חדש' : 'עריכת חוזה'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedContract(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={showCreateModal ? handleCreateContract : handleUpdateContract} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">מידע בסיסי</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">שם החוזה</label>
                      <input
                        type="text"
                        required
                        value={formData.contract_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contract_name: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">חברה</label>
                      <select
                        required
                        value={formData.company_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">בחר חברה</option>
                        {(companies || []).map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">סוג החוזה</label>
                      <select
                        value={formData.contract_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="percentage_discount">הנחה באחוזים</option>
                        <option value="fixed_price">מחיר קבוע</option>
                        <option value="volume_discount">הנחת כמות</option>
                        <option value="tiered_pricing">תמחור מדורג</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">מוצר (אופציונלי)</label>
                      <select
                        value={formData.product_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, product_id: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">כל המוצרים</option>
                        {(products || [])
                          .sort((a, b) => a.name.localeCompare(b.name, 'he'))
                          .map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.sku})
                            </option>
                          ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {loadingProducts ? 'טוען מוצרים...' : `סה"כ ${(products || []).length} מוצרים זמינים`}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">קטגוריה (אופציונלי)</label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">כל הקטגוריות</option>
                        {(categories || []).map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Pricing Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">מידע תמחור</h4>
                    
                    {formData.contract_type === 'fixed_price' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">מחיר קבוע (₪)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.base_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, base_price: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    {formData.contract_type === 'percentage_discount' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">אחוז הנחה (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.discount_percentage}
                          onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">תנאי תשלום (ימים)</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.payment_terms}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) || 0 }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">מסגרת אשראי (₪)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.credit_limit}
                        onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">הזמנה מינימלית (₪)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.minimum_order_value}
                        onChange={(e) => setFormData(prev => ({ ...prev, minimum_order_value: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">משלוח חינם מ- (₪)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.free_shipping_threshold}
                        onChange={(e) => setFormData(prev => ({ ...prev, free_shipping_threshold: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Volume Tiers */}
                {formData.contract_type === 'volume_discount' && (
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-900">דרגות הנחת כמות</h4>
                    
                    {/* Add New Tier */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-4 gap-4 items-end">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">כמות מינימלית</label>
                          <input
                            type="number"
                            min="1"
                            value={newTier.min_qty}
                            onChange={(e) => setNewTier(prev => ({ ...prev, min_qty: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">כמות מקסימלית (אופציונלי)</label>
                          <input
                            type="number"
                            min="1"
                            value={newTier.max_qty}
                            onChange={(e) => setNewTier(prev => ({ ...prev, max_qty: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">אחוז הנחה (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={newTier.discount}
                            onChange={(e) => setNewTier(prev => ({ ...prev, discount: e.target.value }))}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={addVolumeTier}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            הוסף דרגה
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Existing Tiers */}
                    {formData.volume_tiers.length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-gray-700">דרגות קיימות:</h5>
                        {formData.volume_tiers.map((tier, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                            <span className="text-sm">
                              {tier.min_qty} - {tier.max_qty || '∞'} יחידות: {(tier.discount * 100).toFixed(1)}% הנחה
                            </span>
                            <button
                              type="button"
                              onClick={() => removeVolumeTier(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Validity Period */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">תחילת תוקף</label>
                    <input
                      type="date"
                      required
                      value={formData.valid_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, valid_from: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">סוף תוקף (אופציונלי)</label>
                    <input
                      type="date"
                      value={formData.valid_to}
                      onChange={(e) => setFormData(prev => ({ ...prev, valid_to: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Terms and Notes */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">תנאים והגבלות</label>
                    <textarea
                      rows={3}
                      value={formData.terms_and_conditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">הערות פנימיות</label>
                    <textarea
                      rows={2}
                      value={formData.internal_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setSelectedContract(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {showCreateModal ? 'צור חוזה' : 'עדכן חוזה'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContracts;
